import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format } from "date-fns";
import { useDashboard } from "@/contexts/DashboardContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  Calendar,
  X,
  Filter,
} from "lucide-react";
import {
  API,
  formatNumber,
  PLATFORMS,
  isMetricUnavailableForAll,
} from "@/components/dashboard/constants";
import {
  MetricCard,
  DateRangePicker,
  PlatformMultiSelect,
  AccountMultiSelect,
} from "@/components/dashboard/CommonComponents";
import { TopPerformersSection, NeedAttentionSection, EngagementGuide } from "@/components/dashboard/PerformanceSections";
import { PerformanceHeatmap } from "@/components/dashboard/Charts";
import DataEnrichmentStatus from "@/components/dashboard/DataEnrichmentStatus";

const OverviewPage = () => {
  const {
    lang,
    t,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    accounts,
    selectedPlatforms,
    setSelectedPlatforms,
    selectedAccounts,
    setSelectedAccounts,
    summary,
    setSummary,
    loading,
    setLoading,
  } = useDashboard();

  const [heatmapData, setHeatmapData] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const platformParam = selectedPlatforms.length === 0 ? undefined : selectedPlatforms.join(",");
      const accountParam = selectedAccounts.length === 0 ? undefined : selectedAccounts.join(",");
      const start = startDate ? format(startDate, "yyyy-MM-dd") : undefined;
      const end = endDate ? format(endDate, "yyyy-MM-dd") : undefined;

      const [summaryRes, heatmapRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary`, {
          params: { platform: platformParam, account_id: accountParam, start_date: start, end_date: end }
        }),
        // Heatmap should use the same date range as dashboard
        axios.get(`${API}/dashboard/heatmap`, {
          params: { 
            days: Math.min(Math.ceil((new Date(end) - new Date(start)) / (1000*60*60*24)) + 1, 30),
            platform: platformParam, 
            account_id: accountParam 
          }
        }),
      ]);

      setSummary(summaryRes.data);
      setHeatmapData(heatmapRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedPlatforms, selectedAccounts, startDate, endDate, setLoading, setSummary]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Filters Section */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="md:hidden"
        >
          <Filter className="h-4 w-4 mr-2" />
          {lang === 'ko' ? '필터' : lang === 'zh' ? '筛选' : 'Filters'}
        </Button>
        
        <div className={`${showFilters ? 'flex' : 'hidden'} md:flex flex-wrap items-center gap-2 sm:gap-4 w-full md:w-auto`}>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            lang={lang}
          />
          <PlatformMultiSelect
            selected={selectedPlatforms}
            onChange={setSelectedPlatforms}
            lang={lang}
          />
          <AccountMultiSelect
            accounts={accounts}
            selected={selectedAccounts}
            onChange={setSelectedAccounts}
            selectedPlatforms={selectedPlatforms}
            lang={lang}
          />
        </div>
      </div>

      {/* No Data Alert for Date Filter */}
      {summary?.total_posts === 0 && (startDate || endDate) && (
        <Alert className="bg-amber-500/10 border-amber-500/30">
          <Calendar className="h-4 w-4 text-amber-400" />
          <AlertTitle className="text-amber-400">
            {t.dateFilter?.noDataTitle || "No Data for Selected Period"}
          </AlertTitle>
          <AlertDescription className="text-amber-200/80 mt-1">
            <p className="mb-3">{t.dateFilter?.noDataDescription || "There are no posts within the selected date range."}</p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                }}
                className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                data-testid="clear-date-filter-btn"
              >
                <X className="h-3 w-3 mr-1" />
                {t.dateFilter?.clearFilter || "Clear Date Filter"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                  setStartDate(thirtyDaysAgo);
                  setEndDate(new Date());
                }}
                className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                data-testid="extend-date-range-btn"
              >
                <Calendar className="h-3 w-3 mr-1" />
                {t.dateFilter?.extendRange || "Last 30 Days"}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Data Enrichment Status */}
      <DataEnrichmentStatus lang={lang} />

      {/* Metrics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 [&>*:last-child]:col-span-2 sm:[&>*:last-child]:col-span-1">
        {(() => {
          const comparison = summary?.comparison;
          const periodDays = comparison?.period_days || 7;
          const hasData = comparison?.has_previous_data;
          
          // Generate comparison text based on period
          const getComparisonText = () => {
            if (periodDays === 7) {
              return t.metrics.vsPrevPeriod || "vs prev. period";
            }
            const template = t.metrics.vsPrevPeriodDays || "vs prev. {days} days";
            return template.replace("{days}", periodDays);
          };
          
          const comparisonText = getComparisonText();
          const noComparisonText = t.metrics.noComparison || "No comparison data";
          const naText = lang === 'ko' ? 'N/A' : 'N/A';
          const naSubtext = lang === 'ko' ? '해당 플랫폼 미지원' : lang === 'zh' ? '该平台不支持' : 'Not available for platform';
          const activePlatforms = selectedPlatforms.length > 0 ? selectedPlatforms : [];
          
          const sharesNA = isMetricUnavailableForAll("shares", activePlatforms);
          const savesNA = isMetricUnavailableForAll("saves", activePlatforms);
          
          return (
            <>
              <MetricCard
                title={t.metrics.totalViews}
                value={formatNumber(summary?.total_views || 0)}
                icon={Eye}
                trend={hasData ? comparison?.views_change : null}
                subtext={hasData ? comparisonText : noComparisonText}
                noData={!hasData}
                color="#3B82F6"
              />
              <MetricCard
                title={t.metrics.totalLikes}
                value={formatNumber(summary?.total_likes || 0)}
                icon={Heart}
                trend={hasData ? comparison?.likes_change : null}
                subtext={hasData ? comparisonText : noComparisonText}
                noData={!hasData}
                color="#EF4444"
              />
              <MetricCard
                title={t.metrics.comments}
                value={formatNumber(summary?.total_comments || 0)}
                icon={MessageCircle}
                trend={hasData ? comparison?.comments_change : null}
                subtext={hasData ? comparisonText : noComparisonText}
                noData={!hasData}
                color="#F59E0B"
              />
              <MetricCard
                title={t.metrics.shares}
                value={sharesNA ? naText : formatNumber(summary?.total_shares || 0)}
                icon={Share2}
                trend={sharesNA ? null : (hasData ? comparison?.shares_change : null)}
                subtext={sharesNA ? naSubtext : (hasData ? comparisonText : noComparisonText)}
                noData={sharesNA || !hasData}
                color="#10B981"
              />
              <MetricCard
                title={t.metrics.saves}
                value={savesNA ? naText : formatNumber(summary?.total_saves || 0)}
                icon={Bookmark}
                trend={savesNA ? null : (hasData ? comparison?.saves_change : null)}
                subtext={savesNA ? naSubtext : (hasData ? comparisonText : noComparisonText)}
                noData={savesNA || !hasData}
                color="#8B5CF6"
              />
            </>
          );
        })()}
      </div>

      {/* Content Performance Heatmap */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        <Card className="bg-card border-border/50 overflow-visible">
          <CardHeader>
            <CardTitle className="text-lg font-heading">
              {lang === 'ko' ? '콘텐츠 성과 히트맵' : 
               lang === 'zh' ? '内容表现热力图' : 
               'Content Performance Heatmap'}
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-visible">
            <PerformanceHeatmap data={heatmapData} loading={loading} lang={lang} />
          </CardContent>
        </Card>
      </div>

      {/* Engagement Metrics Guide */}
      <EngagementGuide lang={lang} />

      {/* Top Performers & Need Attention */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopPerformersSection
          summary={summary}
          accounts={accounts}
          lang={lang}
          startDate={startDate}
          endDate={endDate}
          accountIds={selectedAccounts}
        />
        <NeedAttentionSection
          summary={summary}
          accounts={accounts}
          lang={lang}
          startDate={startDate}
          endDate={endDate}
          accountIds={selectedAccounts}
        />
      </div>
    </div>
  );
};

export default OverviewPage;
